import { Select } from "antd"
import dayjs from "dayjs"
import _ from "lodash"
import moment from "moment"

// import moment from "moment"
import { type ReactChild } from "#customTypes/globalTypes"
import { type TypesafeAny } from "#customTypes/types"
import { isEmailFieldKey } from "#utils/emailHelpers"

import type { DefaultOptionType } from "antd/es/select"

const { Option } = Select

// Clean out empty values and trim text before submitting.
// `fieldKey` is the object property name when recursing — used to lowercase emails.
export const cleanFormData = (
  submittedData: TypesafeAny,
  fieldKey?: string
): TypesafeAny => {
  // Handle non-object values first
  if (typeof submittedData === "string") {
    const trimmed = submittedData.trim()
    if (trimmed === "") return null
    if (fieldKey && isEmailFieldKey(fieldKey)) {
      return trimmed.toLowerCase()
    }
    return trimmed
  }

  // Handle numbers and booleans - return as-is
  if (typeof submittedData === "number" || typeof submittedData === "boolean") {
    return submittedData
  }

  // Convert undefined/null to null (Firebase accepts null but not undefined)
  if (_.isNil(submittedData)) {
    return null
  }

  // Handle arrays
  if (Array.isArray(submittedData)) {
    const processedArray = submittedData
      .map((item) => cleanFormData(item))
      .filter((item) => {
        // Keep everything except:
        // - null (which we use for cleaned empty values)
        // - empty objects
        // - empty arrays
        if (item === null) return false
        if (_.isObject(item) && _.isEmpty(item)) return false
        return true
      })
    return _.isEmpty(processedArray) ? null : processedArray
  }

  // Handle dayjs/moment objects - return as-is if already valid date objects
  if (dayjs.isDayjs(submittedData) || moment.isMoment(submittedData)) {
    return submittedData.isValid() ? submittedData : null
  }

  // Handle plain objects with _.transform
  if (_.isPlainObject(submittedData)) {
    const result = _.transform(
      submittedData as Record<string, TypesafeAny>,
      (acc: Record<string, TypesafeAny>, value: TypesafeAny, key: string) => {
        const cleanedValue = cleanFormData(value, key)

        // Only add to result if the cleaned value is not null/empty
        if (
          !(_.isObject(cleanedValue) && _.isEmpty(cleanedValue)) ||
          cleanedValue === null
        ) {
          acc[key] = cleanedValue
        }
      }
    )
    return _.isEmpty(result) ? null : result
  }

  // For any other type, return as-is
  return submittedData
}

export const createFormOptions = (
  items: Array<{ id: string; name: string }>
): ReactChild[] => {
  return items.map(({ id, name }) => (
    <Option key={id} value={id}>
      {name}
    </Option>
  ))
}

export const filterFormOptions = (
  input: string,
  option?: DefaultOptionType
): boolean => {
  const labelText = typeof option?.label === "string" ? option.label : ""
  return labelText.toLowerCase().includes(input.toLowerCase())
}
